<template>
  <VChart class="mes-agent-chart" :style="{ height: `${height}px` }" :option="option" autoresize />
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { CustomChart } from 'echarts/charts';
import { use } from 'echarts/core';
import VChart from 'vue-echarts';

import type { AgentRow, AgentViz } from '../../api/agent';
import { registerMesEcharts } from '../../echarts-register';

registerMesEcharts();
use([CustomChart]);

const props = defineProps<{ viz: AgentViz; rows: AgentRow[] }>();

const PALETTE = ['#4f6bed', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];
const num = (v: unknown): number | null => (v == null || v === '' ? null : Number(v));
const shortX = (v: string | number | null | undefined): string => {
  const s = String(v ?? '');
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(s)) return s.slice(5);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s.slice(5);
  return s;
};

const ganttGroups = computed(() => [
  ...new Set(props.rows.map((r) => String(r[props.viz.x] ?? ''))),
]);
const height = computed(() =>
  props.viz.type === 'gantt' ? Math.max(220, ganttGroups.value.length * 44 + 56) : 280,
);

interface GanttApi {
  value: (dim: number) => number;
  coord: (v: [number, number]) => [number, number];
  size: (v: [number, number]) => [number, number];
}

function ganttOption() {
  const { viz } = props;
  const groups = ganttGroups.value;
  const sKey = viz.series[0]?.key ?? '';
  const eKey = viz.series[1]?.key ?? '';
  const items = props.rows.map((r) => ({
    name: String(r[viz.label ?? ''] ?? ''),
    item: String(r['품목'] ?? ''),
    value: [
      groups.indexOf(String(r[viz.x] ?? '')),
      num(r[sKey]) ?? 0,
      num(r[eKey]) ?? 0,
      r['지연'] ? 1 : 0,
    ],
  }));
  const ticks = viz.ticks ?? [];
  const tickMap = new Map(ticks.map((t) => [t.value, t.label]));
  const step = ticks.length > 1 ? (ticks[1]?.value ?? 10) - (ticks[0]?.value ?? 0) : 10;
  return {
    color: PALETTE,
    tooltip: {
      confine: true,
      formatter: (p: { dataIndex: number }) => {
        const it = items[p.dataIndex];
        if (!it) return '';
        return `<b>${it.name}</b><br/>${it.item}<br/>${it.value[1]}h → ${it.value[2]}h${it.value[3] ? ' · <span style="color:#ef4444">납기 지연</span>' : ''}`;
      },
    },
    grid: { left: 8, right: 16, top: 12, bottom: 8, containLabel: true },
    xAxis: {
      type: 'value',
      min: 0,
      max: ticks.length ? ticks[ticks.length - 1]?.value : undefined,
      interval: step,
      axisLabel: { formatter: (v: number) => tickMap.get(v) ?? '' },
      splitLine: { lineStyle: { type: 'dashed' } },
    },
    yAxis: {
      type: 'category',
      data: groups,
      inverse: true,
      axisLabel: { width: 140, overflow: 'truncate' },
    },
    series: [
      {
        type: 'custom',
        encode: { x: [1, 2], y: 0 },
        data: items,
        renderItem: (params: { dataIndex: number }, api: GanttApi) => {
          const cat = api.value(0);
          const start = api.coord([api.value(1), cat]);
          const end = api.coord([api.value(2), cat]);
          const h = api.size([0, 1])[1] * 0.58;
          const width = Math.max(2, end[0] - start[0] - 2);
          const late = api.value(3) === 1;
          return {
            type: 'group',
            children: [
              {
                type: 'rect',
                shape: { x: start[0], y: start[1] - h / 2, width, height: h, r: 4 },
                style: { fill: late ? '#ef4444' : PALETTE[cat % PALETTE.length], opacity: 0.88 },
              },
              {
                type: 'text',
                style: {
                  text: items[params.dataIndex]?.name.replace(/^WO-/, '') ?? '',
                  x: start[0] + 6,
                  y: start[1],
                  verticalAlign: 'middle',
                  fill: '#fff',
                  fontSize: 11,
                  width: Math.max(0, width - 10),
                  overflow: 'truncate',
                },
              },
            ],
          };
        },
      },
    ],
  };
}

const option = computed(() => {
  const { viz } = props;
  if (viz.type === 'gantt') return ganttOption();
  const base = {
    color: PALETTE,
    tooltip: { trigger: viz.type === 'pie' ? 'item' : 'axis', confine: true },
    legend: { top: 0, type: 'scroll' as const },
    animationDuration: 500,
  };

  if (viz.type === 'pie') {
    const key = viz.series[0]?.key ?? '';
    return {
      ...base,
      legend: { bottom: 0, type: 'scroll' as const },
      series: [
        {
          type: 'pie',
          radius: ['42%', '68%'],
          center: ['50%', '45%'],
          itemStyle: { borderRadius: 4, borderColor: '#fff', borderWidth: 2 },
          label: { formatter: '{b}\n{d}%' },
          data: props.rows.map((r) => ({ name: String(r[viz.x]), value: num(r[key]) ?? 0 })),
        },
      ],
    };
  }

  if (viz.type === 'hbar') {
    const k0 = viz.series[0]?.key ?? '';
    const top = [...props.rows]
      .sort((a, b) => (num(b[k0]) ?? 0) - (num(a[k0]) ?? 0))
      .slice(0, 15)
      .reverse();
    return {
      ...base,
      grid: { left: 8, right: 24, top: 28, bottom: 8, containLabel: true },
      xAxis: { type: 'value', name: viz.yLabel },
      yAxis: {
        type: 'category',
        data: top.map((r) => String(r[viz.x])),
        axisLabel: { width: 120, overflow: 'truncate' },
      },
      series: viz.series.map((s) => ({
        name: s.key,
        type: 'bar',
        barMaxWidth: 16,
        data: top.map((r) => num(r[s.key])),
      })),
    };
  }

  // 시계열·범주: x 오름차순 (측정 시각 등은 응답이 최신순)
  const rows =
    viz.type === 'line'
      ? [...props.rows].sort((a, b) => String(a[viz.x]).localeCompare(String(b[viz.x])))
      : props.rows;
  const hasRight = viz.series.some((s) => s.axis === 1);
  // 상한선이 있으면 y축 하한이 상한값을 포함하도록(데이터가 모두 상한 위여도 선이 보이게)
  const firstKey = viz.series[0]?.key ?? '';
  const values = rows.map((r) => num(r[firstKey])).filter((v): v is number => v != null);
  const yMin =
    viz.type === 'line' && viz.markLine && values.length
      ? Math.floor(Math.min(viz.markLine.value, ...values) * 0.97)
      : undefined;
  // 기준선(상한·발주점)이 데이터보다 크면 축을 넓혀 선이 보이게
  const yMax =
    viz.markLine && values.length && viz.markLine.value > Math.max(...values)
      ? Math.ceil(viz.markLine.value * 1.08)
      : undefined;
  return {
    ...base,
    grid: { left: 8, right: hasRight ? 8 : 16, top: 34, bottom: 8, containLabel: true },
    xAxis: {
      type: 'category',
      data: rows.map((r) => shortX(r[viz.x])),
      axisLabel: { hideOverlap: true },
    },
    yAxis: [
      {
        type: 'value',
        name: viz.yLabel,
        scale: viz.type === 'line',
        min: yMin,
        max: yMax,
        splitLine: { lineStyle: { type: 'dashed' } },
      },
      ...(hasRight
        ? [
            {
              type: 'value',
              name: viz.series.find((s) => s.axis === 1)?.key,
              splitLine: { show: false },
            },
          ]
        : []),
    ],
    series: viz.series.map((s, i) => {
      const type = s.type ?? (viz.type === 'line' ? 'line' : 'bar');
      return {
        name: s.key,
        type,
        yAxisIndex: s.axis ?? 0,
        stack: s.stack,
        barMaxWidth: 22,
        smooth: type === 'line' && viz.type !== 'line',
        symbolSize: viz.type === 'line' ? 7 : 5,
        data: rows.map((r) => num(r[s.key])),
        ...(i === 0 && viz.markLine
          ? {
              markLine: {
                symbol: 'none',
                lineStyle: { color: '#ef4444', type: 'dashed', width: 1.5 },
                label: {
                  formatter: viz.markLine.label,
                  position: 'insideEndTop',
                  color: '#ef4444',
                },
                data: [{ yAxis: viz.markLine.value }],
              },
            }
          : {}),
      };
    }),
  };
});
</script>

<style scoped>
.mes-agent-chart {
  width: 100%;
  height: 280px;
}
</style>
